import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Plus, Heart, Sparkles, Users, Trash2, Share2, Trophy, Send,
  ArrowRight, Eye, EyeOff, ChevronRight, ChevronLeft, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const CATEGORIES = [
  { v: "about_me", label: "About Me", emoji: "💁", desc: "Personal favorites & quirks" },
  { v: "love_story", label: "Our Love Story", emoji: "💕", desc: "Memories & milestones" },
  { v: "hubby_wifey", label: "Hubby & Wifey", emoji: "💍", desc: "What would your partner say?" },
  { v: "therapy", label: "Therapy Mode", emoji: "🤍", desc: "Deep reflection (private)" },
  { v: "custom", label: "Custom", emoji: "✨", desc: "Your own questions" },
];

const GAME_MODES = [
  { v: "quick_fire", label: "Quick Fire", desc: "Rapid 30s rounds" },
  { v: "newlywed", label: "Newlywed", desc: "Classic match-your-partner game" },
  { v: "guest_trivia", label: "Guest Trivia", desc: "Guests guess your answers" },
  { v: "family_battle", label: "Family Battle", desc: "Bride vs groom families" },
  { v: "memory_match", label: "Memory Match", desc: "Match memories to dates" },
];

/* ============================================================
   HUB — entry point
   ============================================================ */
export function LockedInHub() {
  const [match, setMatch] = useState(null);
  const [games, setGames] = useState([]);

  useEffect(() => {
    api.get("/locked-in/match").then((r) => setMatch(r.data)).catch(() => {});
    api.get("/locked-in/games/me").then((r) => setGames(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-10" data-testid="locked-in-hub">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Locked In Questions</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl">How well do you know your forever person?</h1>
        <p className="text-[var(--muted)] mt-2 max-w-xl">A guided journey through your relationship — for you, for guests, for keeps.</p>
      </div>

      {/* Stats card */}
      {match && match.matched_questions > 0 && (
        <div className="bg-gradient-to-br from-[#C8A96B]/10 to-transparent border border-[var(--gold-line)] rounded-3xl p-7 md:p-9 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Your match score</p>
          <div className="flex items-end gap-4">
            <p className="font-serif text-6xl md:text-7xl text-[var(--ink)]">{match.average_match}%</p>
            <p className="text-sm text-[var(--muted)] mb-3">across {match.matched_questions} matched questions</p>
          </div>
          <Link to="/dashboard/locked-in/match" className="btn-outline-gold mt-4">See breakdown →</Link>
        </div>
      )}

      {/* Action cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Link to="/dashboard/locked-in/answer" className="group bg-white border border-[var(--line)] rounded-3xl p-7 hover:border-[var(--gold)] transition" data-testid="locked-in-answer-card">
          <Heart className="w-7 h-7 text-[var(--gold)] mb-4" strokeWidth={1.4} />
          <p className="font-serif text-2xl mb-2">Answer Questions</p>
          <p className="text-sm text-[var(--muted)] mb-4">90+ curated prompts across 4 categories. Build your story together.</p>
          <span className="text-[var(--gold)] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">Start <ArrowRight className="w-4 h-4" /></span>
        </Link>
        <Link to="/dashboard/locked-in/games/new" className="group bg-white border border-[var(--line)] rounded-3xl p-7 hover:border-[var(--gold)] transition" data-testid="locked-in-game-card">
          <Sparkles className="w-7 h-7 text-[var(--gold)] mb-4" strokeWidth={1.4} />
          <p className="font-serif text-2xl mb-2">Host a Wedding Game</p>
          <p className="text-sm text-[var(--muted)] mb-4">Generate a code, share with guests, watch the leaderboard light up live.</p>
          <span className="text-[var(--gold)] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">Create game <ArrowRight className="w-4 h-4" /></span>
        </Link>
        <Link to="/dashboard/locked-in/challenges" className="group bg-gradient-to-br from-[var(--ink)] to-black text-white rounded-3xl p-7 hover:shadow-2xl transition" data-testid="locked-in-challenge-card">
          <Sparkles className="w-7 h-7 text-[var(--gold)] mb-4" strokeWidth={1.4} />
          <p className="font-serif text-2xl mb-2">Couple-vs-Couple Challenge</p>
          <p className="text-sm text-white/70 mb-4">Face off against another couple. Same questions, head-to-head reveal.</p>
          <span className="text-[var(--gold)] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">Open challenges <ArrowRight className="w-4 h-4" /></span>
        </Link>
      </div>

      {/* Recent games */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-2xl">Your games</p>
          <Link to="/dashboard/locked-in/games/new" className="btn-gold text-xs"><Plus className="w-3 h-3" /> New game</Link>
        </div>
        {games.length === 0 ? (
          <div className="text-center py-12 bg-[var(--bg-warm)] rounded-2xl">
            <Users className="w-7 h-7 mx-auto text-[var(--gold)] mb-2" />
            <p className="text-sm text-[var(--muted)]">No games yet. Create your first one above.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {games.map((g) => (
              <div key={g.game_id} className="bg-white border border-[var(--line)] rounded-2xl p-5 hover:border-[var(--gold)] transition" data-testid={`game-card-${g.code}`}>
                <Link to={`/dashboard/locked-in/games/${g.game_id}`} className="block">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">{g.mode.replaceAll("_", " ")}</p>
                  <p className="font-serif text-xl mt-1">{g.title}</p>
                  <p className="text-xs text-[var(--muted)] mt-2">Code: <strong className="font-mono">{g.code}</strong> · {g.question_ids.length} questions</p>
                  <p className="text-xs mt-2">{g.is_open ? <span className="text-green-700">● Open</span> : <span className="text-[var(--muted)]">Closed</span>}</p>
                </Link>
                <a
                  href={`/reception/${g.code}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] hover:underline"
                  data-testid={`reception-link-${g.code}`}
                >
                  Open reception screen →
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ANSWER — couple answers questions
   ============================================================ */
export function LockedInAnswerPage() {
  const [activeCat, setActiveCat] = useState("about_me");
  const [activeRole, setActiveRole] = useState("partner_one");
  const [questions, setQuestions] = useState([]);
  const [myAnswers, setMyAnswers] = useState({});
  const [draft, setDraft] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [customText, setCustomText] = useState("");

  function loadQuestions(cat) {
    api.get(`/locked-in/questions?category=${cat}`).then((r) => setQuestions(r.data || [])).catch(() => {});
  }

  function loadAnswers() {
    api.get("/locked-in/answers/me").then((r) => {
      const map = {};
      (r.data || []).forEach((a) => {
        map[`${a.question_id}|${a.partner_role}`] = a.answer;
      });
      setMyAnswers(map);
    }).catch(() => {});
  }

  useEffect(() => loadQuestions(activeCat), [activeCat]);
  useEffect(() => loadAnswers(), []);

  async function save(qid) {
    const text = (draft[qid] || "").trim();
    if (!text) return toast.error("Type an answer first");
    try {
      await api.post("/locked-in/answers", { question_id: qid, answer: text, partner_role: activeRole });
      toast.success("Saved");
      setMyAnswers((m) => ({ ...m, [`${qid}|${activeRole}`]: text }));
      setDraft((d) => ({ ...d, [qid]: "" }));
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function addCustom(e) {
    e.preventDefault();
    if (!customText.trim()) return;
    try {
      await api.post("/locked-in/questions/custom", { text: customText, category: activeCat === "therapy" ? "therapy" : "custom" });
      toast.success("Custom question added");
      setCustomText("");
      setShowAdd(false);
      loadQuestions(activeCat);
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function removeCustom(qid) {
    if (!window.confirm("Delete this custom question?")) return;
    try {
      await api.delete(`/locked-in/questions/${qid}`);
      loadQuestions(activeCat);
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="space-y-6" data-testid="locked-in-answer">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Locked In Questions</p>
        <h1 className="font-serif font-light text-4xl">Answer together. Share the story.</h1>
      </div>

      {/* Role switcher */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mr-2">Answering as:</span>
        {[
          { v: "partner_one", label: "Partner 1" },
          { v: "partner_two", label: "Partner 2" },
        ].map((r) => (
          <button key={r.v} onClick={() => setActiveRole(r.v)} data-testid={`role-${r.v}`} className={`px-4 py-1.5 text-xs rounded-full border transition ${activeRole === r.v ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`}>
            {r.label}
          </button>
        ))}
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 border-b border-[var(--line)] pb-3">
        {CATEGORIES.map((c) => (
          <button key={c.v} onClick={() => setActiveCat(c.v)} data-testid={`cat-${c.v}`} className={`px-4 py-2 text-sm rounded-xl border transition ${activeCat === c.v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] hover:border-[var(--gold)]"}`}>
            <span className="mr-1">{c.emoji}</span> {c.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--muted)]">{questions.length} questions in {CATEGORIES.find((c) => c.v === activeCat)?.label}</p>
        <button onClick={() => setShowAdd(true)} data-testid="add-custom-q" className="btn-outline-gold text-xs"><Plus className="w-3 h-3" /> Add custom</button>
      </div>

      {/* Question list */}
      <div className="space-y-4">
        {questions.map((q) => {
          const saved = myAnswers[`${q.question_id}|${activeRole}`] || "";
          return (
            <div key={q.question_id} className="bg-white border border-[var(--line)] rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3">
                <p className="font-serif text-lg">{q.text}</p>
                {!q.is_seed && q.created_by && (
                  <button onClick={() => removeCustom(q.question_id)} className="text-[var(--muted)] hover:text-red-600 shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {saved && (
                <p className="text-xs text-[var(--gold)] mt-2 flex items-center gap-1"><Check className="w-3 h-3" /> Saved: <span className="text-[var(--ink-soft)] italic">"{saved}"</span></p>
              )}
              <div className="flex gap-2 mt-3">
                <input
                  data-testid={`answer-input-${q.question_id}`}
                  value={draft[q.question_id] ?? ""}
                  onChange={(e) => setDraft({ ...draft, [q.question_id]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && save(q.question_id)}
                  placeholder={saved ? "Update your answer…" : "Type your answer…"}
                  className="flex-1 border border-[var(--line)] rounded-xl px-4 py-2 text-sm bg-[var(--bg-warm)]"
                />
                <button onClick={() => save(q.question_id)} data-testid={`save-${q.question_id}`} className="btn-gold text-xs">Save</button>
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowAdd(false)}>
          <form onSubmit={addCustom} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-serif text-2xl">Add custom question</p>
              <button type="button" onClick={() => setShowAdd(false)}><X className="w-5 h-5" /></button>
            </div>
            <input required value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="What's the funniest text I've ever sent?" className="w-full border border-[var(--line)] rounded-xl px-3 py-2" data-testid="custom-text" />
            <button type="submit" className="btn-gold w-full justify-center" data-testid="custom-save">Add</button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   MATCH — side-by-side answer comparison
   ============================================================ */
export function LockedInMatchPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/locked-in/match").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-[var(--muted)]">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="locked-in-match">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Match Breakdown</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl">{data.couple_names}</h1>
      </div>

      {/* Big score */}
      <div className="text-center py-10 bg-gradient-to-b from-[#C8A96B]/10 to-transparent rounded-3xl">
        <p className="font-serif text-7xl md:text-8xl">{data.average_match}%</p>
        <p className="text-[var(--muted)] mt-2">average match across {data.matched_questions} matched questions</p>
      </div>

      {data.matched_questions === 0 ? (
        <div className="text-center py-12 bg-[var(--bg-warm)] rounded-2xl">
          <p className="text-sm text-[var(--muted)]">No matched answers yet. Both partners need to answer the same question.</p>
          <Link to="/dashboard/locked-in/answer" className="btn-gold mt-4 inline-flex">Start answering →</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {data.rows.map((row) => (
            <div key={row.question_id} className="bg-white border border-[var(--line)] rounded-2xl p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="font-serif text-lg flex-1">{row.question}</p>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                  row.score >= 80 ? "bg-green-100 text-green-800" :
                  row.score >= 50 ? "bg-amber-100 text-amber-800" :
                  "bg-red-100 text-red-700"
                }`}>{row.score}%</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-[var(--bg-warm)] rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1">Partner 1</p>
                  <p>{row.partner_one}</p>
                </div>
                <div className="bg-[var(--bg-warm)] rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1">Partner 2</p>
                  <p>{row.partner_two}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   GAME CREATOR
   ============================================================ */
export function LockedInGameCreate() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("Reception Trivia");
  const [mode, setMode] = useState("guest_trivia");
  const [activeCat, setActiveCat] = useState("about_me");
  const [questions, setQuestions] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/locked-in/questions?category=${activeCat}`).then((r) => setQuestions(r.data || [])).catch(() => {});
  }, [activeCat]);

  function toggle(qid) {
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(qid)) next.delete(qid);
      else if (next.size < 30) next.add(qid);
      else toast.error("Max 30 questions per game");
      return next;
    });
  }

  async function create() {
    if (picked.size === 0) return toast.error("Pick at least one question");
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const { data } = await api.post("/locked-in/games", {
        title: title.trim(),
        mode,
        question_ids: Array.from(picked),
      });
      toast.success(`Game ready! Code: ${data.code}`);
      navigate(`/dashboard/locked-in/games/${data.game_id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  }

  return (
    <div className="space-y-8" data-testid="locked-in-game-create">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">New Game</p>
        <h1 className="font-serif font-light text-4xl">Build your game</h1>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-3xl p-6 space-y-5 max-w-3xl">
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Game title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full mt-1 border border-[var(--line)] rounded-xl px-4 py-2" data-testid="game-title" />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2">Game mode</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {GAME_MODES.map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setMode(m.v)}
                data-testid={`mode-${m.v}`}
                className={`text-left p-3 rounded-xl border transition ${mode === m.v ? "border-[var(--gold)] bg-[#C8A96B]/5" : "border-[var(--line)] hover:border-[var(--gold)]"}`}
              >
                <p className="font-medium text-sm">{m.label}</p>
                <p className="text-xs text-[var(--muted)] mt-0.5">{m.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-2xl">Pick questions</p>
          <p className="text-sm text-[var(--muted)]">{picked.size} selected (max 30)</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {CATEGORIES.filter((c) => c.v !== "therapy").map((c) => (
            <button key={c.v} onClick={() => setActiveCat(c.v)} data-testid={`pick-cat-${c.v}`} className={`px-3 py-1.5 text-xs rounded-full border ${activeCat === c.v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)]"}`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {questions.map((q) => (
            <button
              key={q.question_id}
              onClick={() => toggle(q.question_id)}
              data-testid={`pick-q-${q.question_id}`}
              className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition ${picked.has(q.question_id) ? "border-[var(--gold)] bg-[#C8A96B]/5" : "border-[var(--line)] bg-white hover:border-[var(--gold)]"}`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${picked.has(q.question_id) ? "bg-[var(--gold)] text-white" : "border border-[var(--line)]"}`}>
                {picked.has(q.question_id) && <Check className="w-3 h-3" />}
              </div>
              <span className="text-sm">{q.text}</span>
            </button>
          ))}
        </div>
      </div>

      <button onClick={create} disabled={busy || picked.size === 0} data-testid="game-create-submit" className="btn-gold sticky bottom-4">
        {busy ? "Creating…" : `Create game with ${picked.size} questions`} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ============================================================
   GAME HOST view (live)
   ============================================================ */
export function LockedInGameHost() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [board, setBoard] = useState(null);
  const [questionMap, setQuestionMap] = useState({});
  const [results, setResults] = useState({});
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get("/locked-in/games/me").then((r) => {
      const g = (r.data || []).find((x) => x.game_id === id);
      setGame(g);
    });
  }, [id, refresh]);

  useEffect(() => {
    if (!game) return;
    api.get(`/public/play/${game.code}/leaderboard`).then((r) => setBoard(r.data));
    api.get(`/public/play/${game.code}`).then((r) => {
      const map = {};
      (r.data.questions || []).forEach((q) => { map[q.question_id] = q.text; });
      setQuestionMap(map);
    });
    // Fetch results for each revealed question
    const fetchResults = async () => {
      const out = {};
      for (const idx of (game.revealed_indices || [])) {
        const qid = game.question_ids[idx];
        try {
          const { data } = await api.get(`/public/play/${game.code}/results/${qid}`);
          if (data.revealed) out[qid] = data;
        } catch {}
      }
      setResults(out);
    };
    fetchResults();
    const t = setInterval(() => {
      api.get(`/public/play/${game.code}/leaderboard`).then((r) => setBoard(r.data)).catch(() => {});
    }, 3500);
    return () => clearInterval(t);
  }, [game]);

  async function reveal(idx) {
    try {
      await api.post(`/locked-in/games/${id}/reveal/${idx}`);
      setRefresh((r) => r + 1);
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function close() {
    if (!window.confirm("Close this game? Guests won't be able to vote anymore.")) return;
    await api.post(`/locked-in/games/${id}/close`);
    setRefresh((r) => r + 1);
  }
  async function remove() {
    if (!window.confirm("Delete this game permanently?")) return;
    await api.delete(`/locked-in/games/${id}`);
    window.location.href = "/dashboard/locked-in";
  }

  if (!game) return <p className="text-[var(--muted)]">Loading…</p>;

  const playUrl = `${window.location.origin}/play/${game.code}`;

  return (
    <div className="space-y-8" data-testid="locked-in-game-host">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">{game.mode.replaceAll("_", " ")}</p>
          <h1 className="font-serif font-light text-4xl">{game.title}</h1>
        </div>
        <div className="flex gap-2">
          {game.is_open ? (
            <button onClick={close} className="btn-outline-gold text-xs"><EyeOff className="w-3 h-3" /> Close</button>
          ) : <span className="text-xs text-[var(--muted)]">Closed</span>}
          <button onClick={remove} className="text-[var(--muted)] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Share card */}
      <div className="bg-gradient-to-br from-[var(--ink)] to-black text-white rounded-3xl p-7 md:p-9">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3">Share this code with guests</p>
        <p className="font-serif text-7xl md:text-8xl tracking-[0.18em] text-[var(--gold)]" data-testid="game-code">{game.code}</p>
        <p className="text-sm mt-3 text-white/70">Or share the link: <span className="font-mono text-white">{playUrl}</span></p>
        <div className="flex gap-3 mt-5">
          <button onClick={() => { navigator.clipboard.writeText(playUrl); toast.success("Link copied"); }} className="btn-gold text-xs"><Share2 className="w-3 h-3" /> Copy link</button>
          <a href={playUrl} target="_blank" rel="noreferrer" className="btn-outline-gold text-xs">Open guest view ↗</a>
        </div>
      </div>

      {/* Leaderboard */}
      {board && (
        <div className="bg-white border border-[var(--line)] rounded-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="font-serif text-2xl flex items-center gap-2"><Trophy className="w-5 h-5 text-[var(--gold)]" /> Leaderboard</p>
            <p className="text-xs text-[var(--muted)]">{board.guests.length} guest{board.guests.length === 1 ? "" : "s"}</p>
          </div>
          {Object.keys(board.side_scores || {}).length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-5 text-center text-sm">
              {Object.entries(board.side_scores).map(([side, sc]) => (
                <div key={side} className="bg-[var(--bg-warm)] rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">{side}</p>
                  <p className="font-serif text-2xl mt-1">{sc}</p>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {board.guests.length === 0 && <p className="text-sm text-[var(--muted)]">No guests yet. Share the code!</p>}
            {board.guests.map((g, i) => (
              <div key={g.name + i} className="flex items-center gap-3 p-3 bg-[var(--bg-warm)] rounded-xl text-sm">
                <span className="font-serif text-lg w-6 text-[var(--gold)]">{i + 1}</span>
                <span className="flex-1">{g.name} <span className="text-xs text-[var(--muted)] ml-1">({g.side})</span></span>
                <span className="font-medium">{g.score} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Question reveal panel */}
      <div>
        <p className="font-serif text-2xl mb-4">Questions ({game.question_ids.length})</p>
        <div className="space-y-3">
          {game.question_ids.map((qid, idx) => {
            const revealed = (game.revealed_indices || []).includes(idx);
            const r = results[qid];
            return (
              <div key={qid} className="bg-white border border-[var(--line)] rounded-2xl p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Q{idx + 1}</p>
                  {revealed ? (
                    <span className="text-xs text-green-700 flex items-center gap-1"><Eye className="w-3 h-3" /> Revealed</span>
                  ) : (
                    <button onClick={() => reveal(idx)} data-testid={`reveal-${idx}`} className="btn-outline-gold text-xs">Reveal answers</button>
                  )}
                </div>
                <p className="font-serif text-lg">{questionMap[qid] || qid}</p>
                {revealed && r && (
                  <div className="mt-4 space-y-2">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Vote distribution ({r.total_votes} votes)</p>
                    {r.groups.length === 0 && <p className="text-sm text-[var(--muted)]">No votes yet</p>}
                    {r.groups.map((g, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="flex-1">
                          <p className="font-medium">{g.answer}</p>
                          <p className="text-xs text-[var(--muted)]">{g.voters.map((v) => v.name).slice(0, 5).join(", ")}{g.voters.length > 5 ? ` +${g.voters.length - 5} more` : ""}</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-[var(--bg-warm)] rounded-full">{g.count}</span>
                      </div>
                    ))}
                    {r.couple_answers?.length > 0 && (
                      <div className="mt-3 p-3 bg-[#C8A96B]/10 rounded-xl">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Couple's answers</p>
                        {r.couple_answers.map((ca, i) => (
                          <p key={i} className="text-sm">{ca.partner_role.replace("_", " ")}: <strong>{ca.answer}</strong></p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
